import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createLedger, updateLedger, fetchGroupCategories, fetchPrimaryGroups, fetchSubGroups, fetchLedgerNextSequencePreview } from '../../store/coaStore/coaThunk';
import { ApiStatus } from '../../utils/api.types';
import { ILedger } from '../../utils/coa.types';
import { toast } from 'react-toastify';

interface LedgerFormProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  editData?: ILedger | null;
}

const schema = yup.object().shape({
  ledgerName: yup.string().required('Ledger Name is required').min(2, 'Ledger Name must be at least 2 characters').max(100, 'Ledger Name must not exceed 100 characters'),
});

const LedgerForm: FC<LedgerFormProps> = ({ open, onClose, editData }) => {
  const dispatch = useAppDispatch();
  const { groupCategories, primaryGroups, subGroups, status } = useAppSelector(state => state.coa);
  const [selectedGroupCategory, setSelectedGroupCategory] = useState('');
  const [selectedPrimaryGroup, setSelectedPrimaryGroup] = useState('');
  const [selectedSubGroup, setSelectedSubGroup] = useState('');
  const [filteredPrimaryGroups, setFilteredPrimaryGroups] = useState(primaryGroups);
  const [filteredSubGroups, setFilteredSubGroups] = useState(subGroups);
  const [previewAccountingCode, setPreviewAccountingCode] = useState('Auto');

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      ledgerName: '',
    }
  });

  const { handleSubmit, reset, formState: { errors } } = methods;

  useEffect(() => {
    if (open) {
      dispatch(fetchGroupCategories({ limit: 100 }));
      dispatch(fetchPrimaryGroups({ limit: 100 }));
      dispatch(fetchSubGroups({ limit: 100 }));
    }
  }, [dispatch, open]);

  // Filter Primary Groups based on Group Category
  useEffect(() => {
    if (selectedGroupCategory) {
      const filtered = primaryGroups.filter((pg: any) => {
        const categoryId = typeof pg.groupCategory === 'object' ? pg.groupCategory?._id : pg.groupCategory;
        return categoryId === selectedGroupCategory;
      });
      setFilteredPrimaryGroups(filtered);
    } else {
      setFilteredPrimaryGroups(primaryGroups);
    }
  }, [selectedGroupCategory, primaryGroups]);

  // Filter Sub Groups based on Primary Group
  useEffect(() => {
    if (selectedPrimaryGroup) {
      const filtered = subGroups.filter((sg: any) => {
        const primaryId = typeof sg.primaryGroup === 'object' ? sg.primaryGroup?._id : sg.primaryGroup;
        return primaryId === selectedPrimaryGroup;
      });
      setFilteredSubGroups(filtered);
    } else {
      setFilteredSubGroups(subGroups);
    }
  }, [selectedPrimaryGroup, subGroups]);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      reset({
        ledgerName: editData.ledgerName || editData.name || '',
      });

      const categoryId = typeof editData.groupCategory === 'object'
        ? (editData.groupCategory as any)?._id
        : editData.groupCategory;
      setSelectedGroupCategory(categoryId || '');

      const primaryId = typeof editData.primaryGroup === 'object'
        ? (editData.primaryGroup as any)?._id
        : editData.primaryGroup;
      setSelectedPrimaryGroup(primaryId || '');

      const subGroupId = typeof editData.subGroup === 'object'
        ? (editData.subGroup as any)?._id
        : editData.subGroup;
      setSelectedSubGroup(subGroupId || '');

      setPreviewAccountingCode(editData.accountingCode || 'Auto');
    } else {
      reset({
        ledgerName: '',
      });
      setSelectedGroupCategory('');
      setSelectedPrimaryGroup('');
      setSelectedSubGroup('');
      setPreviewAccountingCode('Auto');
    }
  }, [editData, reset, open]);

  // Fetch next sequence when Primary Group changes (only for new entries)
  useEffect(() => {
    if (!editData && selectedPrimaryGroup) {
      dispatch(fetchLedgerNextSequencePreview({ primaryGroupId: selectedPrimaryGroup, subGroupId: selectedSubGroup || undefined }))
        .unwrap()
        .then((result) => {
          setPreviewAccountingCode(result.accountingCode);
        })
        .catch(() => {
          setPreviewAccountingCode('Auto');
        });
    }
  }, [selectedPrimaryGroup, selectedSubGroup, editData, dispatch]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        ...data,
        groupCategory: selectedGroupCategory || undefined,
        primaryGroup: selectedPrimaryGroup || undefined,
        subGroup: selectedSubGroup || undefined,
      };

      if (editData?._id) {
        await dispatch(updateLedger({ id: editData._id, payload })).unwrap();
      } else {
        await dispatch(createLedger(payload)).unwrap();
      }
      reset();
      onClose(true);
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong');
    }
  };

  const handleClose = () => {
    reset();
    setSelectedGroupCategory('');
    setSelectedPrimaryGroup('');
    setSelectedSubGroup('');
    setPreviewAccountingCode('Auto');
    onClose(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 550 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editData ? 'Edit Ledger' : 'Add Ledger'}
            </Typography>
          </Box>

          {/* Content - Scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group Category</InputLabel>
                  <Select
                    value={selectedGroupCategory}
                    label="Group Category"
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedGroupCategory(e.target.value);
                      setSelectedPrimaryGroup('');
                      setSelectedSubGroup('');
                      setPreviewAccountingCode('Auto');
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {groupCategories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name} {cat.accountingCode ? `(${cat.accountingCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Primary Group</InputLabel>
                  <Select
                    value={selectedPrimaryGroup}
                    label="Primary Group"
                    disabled={!selectedGroupCategory}
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedPrimaryGroup(e.target.value);
                      setSelectedSubGroup('');
                      setPreviewAccountingCode('Auto');
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredPrimaryGroups.map((pg: any) => (
                      <MenuItem key={pg._id} value={pg._id}>
                        {pg.name} {pg.accountingCode ? `(${pg.accountingCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedGroupCategory && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Please select a Group Category first
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sub Group</InputLabel>
                  <Select
                    value={selectedSubGroup}
                    label="Sub Group"
                    disabled={!selectedPrimaryGroup}
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedSubGroup(e.target.value);
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredSubGroups.map((sg: any) => (
                      <MenuItem key={sg._id} value={sg._id}>
                        {sg.name} {sg.accountingCode ? `(${sg.accountingCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedPrimaryGroup && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Please select a Primary Group first
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="ledgerName"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Ledger Name"
                      fullWidth
                      required
                      error={!!errors.ledgerName}
                      helperText={errors.ledgerName?.message}
                      placeholder="e.g., Cash in Hand, Sales Account, Rent Expense"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontStyle: 'italic'
                  }}
                >
                  Sequence number and accounting code will be auto-generated upon saving.
                </Typography>
                {previewAccountingCode !== 'Auto' && (
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 600,
                      mt: 0.5,
                      fontSize: '1rem'
                    }}
                  >
                    Accounting Code: {previewAccountingCode}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box sx={{
            borderTop: '1px solid #e5e7eb',
            p: 2,
            backgroundColor: '#f9fafb',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={status === ApiStatus.LOADING}
              sx={{
                minWidth: 100,
                height: 40,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{
                minWidth: 140,
                height: 40,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {editData ? 'Update' : 'Save'}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default LedgerForm;
